import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://healthcare.visionkraftconsulting.com/api'
  : 'http://localhost:3025/api';

const InvoicePreviewPage = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailForm, setEmailForm] = useState({
    recipientEmail: '',
    recipientName: '',
    message: '',
    ccEmails: '',
    bccEmails: ''
  });
  const [messageTemplate, setMessageTemplate] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  // Helper function to get invoice number
  const getInvoiceNumber = (inv) => {
    return inv?.invoice_number || inv?.filename?.replace('.pdf', '') || `HUHC${inv?.id || id}`;
  };

  // Helper function to process message with current invoice number
  const processMessageWithInvoiceNumber = (template, inv) => {
    if (!template) return '';
    const invoiceNumber = getInvoiceNumber(inv);
    return template.replace(/{INVOICE_NUMBER}/g, invoiceNumber);
  };

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/billing/invoice/preview/${id}`);
      
      if (response.data.success) {
        setInvoice(response.data.invoice);
        
        // Store the template and process it
        const template = response.data.invoice.default_email_message || '';
        setMessageTemplate(template);
        const processedMessage = processMessageWithInvoiceNumber(template, response.data.invoice);
        
        // Build CC list including payment advisor
        let ccList = [];
        if (response.data.invoice.default_cc_emails) {
          ccList = response.data.invoice.default_cc_emails.split(',').map(e => e.trim()).filter(e => e);
        }
        if (response.data.invoice.payment_advisor_email && !ccList.includes(response.data.invoice.payment_advisor_email)) {
          ccList.push(response.data.invoice.payment_advisor_email);
        }
        
        setEmailForm({
          recipientEmail: response.data.invoice.bill_to_email || '',
          recipientName: response.data.invoice.bill_to_name || '',
          message: processedMessage,
          ccEmails: ccList.join(', '),
          bccEmails: response.data.invoice.default_bcc_emails || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch invoice:', err);
      setError('Failed to load invoice preview');
    } finally {
      setLoading(false);
    }
  };

  // Handle message change with dynamic invoice number
  const handleMessageChange = (newMessage) => {
    setEmailForm({ ...emailForm, message: newMessage });
  };

  // Reset message to template
  const resetMessageToTemplate = () => {
    if (messageTemplate && invoice) {
      const processedMessage = processMessageWithInvoiceNumber(messageTemplate, invoice);
      setEmailForm({ ...emailForm, message: processedMessage });
    }
  };

  const sendEmail = async () => {
    setSendingEmail(true);
    setError('');
    setEmailSuccess('');
    
    try {
      // Process the message one more time before sending to ensure invoice number is current
      const finalMessage = emailForm.message.includes('{INVOICE_NUMBER}') 
        ? processMessageWithInvoiceNumber(emailForm.message, invoice)
        : emailForm.message;
      
      const response = await axios.post(
        `${API_BASE_URL}/billing/invoice/send-email/${id}`,
        { ...emailForm, message: finalMessage }
      );
      
      if (response.data.success) {
        setEmailSuccess(`Invoice sent successfully to ${response.data.recipient}`);
        // Don't clear pre-filled defaults, just clear the success message after a delay
        setTimeout(() => setEmailSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Failed to send email:', err);
      setError(err.response?.data?.message || 'Failed to send invoice email');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ 
        backgroundColor: '#0a0a0a', 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#e0e0e0'
      }}>
        <p>Loading invoice...</p>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="container" style={{ 
        backgroundColor: '#0a0a0a', 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center'
      }}>
        <p className="error" style={{ color: '#ff5252' }}>{error}</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="container" style={{ 
        backgroundColor: '#0a0a0a', 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#e0e0e0'
      }}>
        <p>Invoice not found</p>
      </div>
    );
  }

  const totalAmount = (invoice.hours * invoice.unit_price).toFixed(2);

  return (
    <div className="container" style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '20px',
      backgroundColor: '#0a0a0a',
      minHeight: '100vh',
      color: '#e0e0e0'
    }}>
      <h2 style={{ 
        marginBottom: '30px', 
        fontSize: '28px', 
        color: '#ffffff',
        borderBottom: '2px solid #1e88e5',
        paddingBottom: '10px'
      }}>Invoice Preview</h2>
      
      <div style={{ 
        marginBottom: '30px',
        display: 'flex',
        gap: '15px',
        flexWrap: 'wrap'
      }}>
        <a 
          href={invoice.s3_url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="button"
          style={{ 
            backgroundColor: '#1e88e5',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '6px',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
            border: '1px solid #1565c0'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          View PDF
        </a>
        <a 
          href={invoice.s3_url} 
          download={invoice.filename}
          className="button"
          style={{ 
            backgroundColor: '#00897b',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '6px',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
            border: '1px solid #00695c'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download PDF
        </a>
      </div>

      {/* Embedded PDF Preview */}
      {invoice.s3_url && (
        <div style={{ 
          marginBottom: '40px',
          border: '1px solid #2a2a2a',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#1a1a1a',
          boxShadow: '0 4px 6px rgba(0,0,0,0.8)'
        }}>
          <iframe
            src={invoice.s3_url}
            width="100%"
            height="600px"
            title="Invoice PDF Preview"
            style={{ border: 'none' }}
            onError={(e) => {
              console.error('Failed to load PDF in iframe:', e);
            }}
          />
          <div style={{
            padding: '10px',
            textAlign: 'center',
            fontSize: '12px',
            color: '#888',
            backgroundColor: '#0f0f0f'
          }}>
            If the preview doesn't load, use the View PDF button above to open in a new tab.
          </div>
        </div>
      )}

      <div className="invoice-details" style={{ 
        border: '1px solid #2a2a2a', 
        padding: '30px', 
        marginBottom: '40px',
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.5)'
      }}>
        <h3 style={{ 
          marginBottom: '20px',
          color: '#ffffff',
          fontSize: '22px',
          borderBottom: '1px solid #333',
          paddingBottom: '10px'
        }}>Invoice Details</h3>
        <p style={{ color: '#e0e0e0' }}><strong style={{ color: '#1e88e5' }}>Invoice #:</strong> {invoice.invoice_number || invoice.filename?.replace('.pdf', '') || `HUHC${invoice.id}`}</p>
        <p style={{ color: '#e0e0e0' }}><strong style={{ color: '#1e88e5' }}>Bill To:</strong> {invoice.bill_to_name}</p>
        <p style={{ color: '#e0e0e0' }}><strong style={{ color: '#1e88e5' }}>Email:</strong> {invoice.bill_to_email}</p>
        <p style={{ color: '#e0e0e0' }}><strong style={{ color: '#1e88e5' }}>Phone:</strong> {invoice.bill_to_phone}</p>
        <p style={{ color: '#e0e0e0' }}><strong style={{ color: '#1e88e5' }}>Service:</strong> {invoice.service_description}</p>
        <p style={{ color: '#e0e0e0' }}><strong style={{ color: '#1e88e5' }}>Hours:</strong> {invoice.hours}</p>
        <p style={{ color: '#e0e0e0' }}><strong style={{ color: '#1e88e5' }}>Rate:</strong> ${invoice.unit_price}/hour</p>
        <p style={{ color: '#e0e0e0' }}><strong style={{ color: '#1e88e5' }}>Total:</strong> ${totalAmount}</p>
        <p style={{ color: '#e0e0e0' }}><strong style={{ color: '#1e88e5' }}>Due Date:</strong> {invoice.due_date}</p>
      </div>

      <div className="email-section" style={{
        backgroundColor: '#1a1a1a',
        padding: '30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
        border: '1px solid #2a2a2a'
      }}>
        <h3 style={{ 
          marginBottom: '20px',
          color: '#ffffff',
          fontSize: '22px',
          borderBottom: '1px solid #333',
          paddingBottom: '10px'
        }}>Send Invoice via Email</h3>
        
        {emailSuccess && (
          <div className="success-message" style={{ 
            backgroundColor: '#1b5e20', 
            border: '1px solid #2e7d32',
            color: '#a5d6a7',
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '4px'
          }}>
            {emailSuccess}
          </div>
        )}
        
        {error && (
          <div className="error-message" style={{ 
            backgroundColor: '#b71c1c', 
            border: '1px solid #d32f2f',
            color: '#ffcdd2',
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '4px'
          }}>
            {error}
          </div>
        )}

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#e0e0e0'
          }}>To:</label>
          <input
            type="email"
            value={emailForm.recipientEmail}
            onChange={(e) => setEmailForm({ ...emailForm, recipientEmail: e.target.value })}
            placeholder="recipient@example.com"
            style={{
              width: '100%',
              padding: '10px 15px',
              border: '1px solid #333',
              borderRadius: '6px',
              fontSize: '16px',
              transition: 'border-color 0.3s ease',
              backgroundColor: '#0f0f0f',
              color: '#e0e0e0'
            }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#e0e0e0'
          }}>Recipient Name:</label>
          <input
            type="text"
            value={emailForm.recipientName}
            onChange={(e) => setEmailForm({ ...emailForm, recipientName: e.target.value })}
            placeholder="John Doe"
            style={{
              width: '100%',
              padding: '10px 15px',
              border: '1px solid #333',
              borderRadius: '6px',
              fontSize: '16px',
              transition: 'border-color 0.3s ease',
              backgroundColor: '#0f0f0f',
              color: '#e0e0e0'
            }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#e0e0e0'
          }}>
            CC (comma separated):
            {invoice.default_cc_emails && (
              <span style={{ 
                fontWeight: 'normal',
                fontSize: '14px',
                color: '#00897b',
                marginLeft: '10px'
              }}>
                (Auto-filled with defaults)
              </span>
            )}
          </label>
          <input
            type="text"
            value={emailForm.ccEmails}
            onChange={(e) => setEmailForm({ ...emailForm, ccEmails: e.target.value })}
            placeholder="cc1@example.com, cc2@example.com"
            style={{
              width: '100%',
              padding: '10px 15px',
              border: '1px solid #333',
              borderRadius: '6px',
              fontSize: '16px',
              transition: 'border-color 0.3s ease',
              backgroundColor: '#0f0f0f',
              color: '#e0e0e0'
            }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#e0e0e0'
          }}>
            BCC (comma separated):
            {invoice.default_bcc_emails && (
              <span style={{ 
                fontWeight: 'normal',
                fontSize: '14px',
                color: '#00897b',
                marginLeft: '10px'
              }}>
                (Auto-filled with defaults)
              </span>
            )}
          </label>
          <input
            type="text"
            value={emailForm.bccEmails}
            onChange={(e) => setEmailForm({ ...emailForm, bccEmails: e.target.value })}
            placeholder="bcc1@example.com, bcc2@example.com"
            style={{
              width: '100%',
              padding: '10px 15px',
              border: '1px solid #333',
              borderRadius: '6px',
              fontSize: '16px',
              transition: 'border-color 0.3s ease',
              backgroundColor: '#0f0f0f',
              color: '#e0e0e0'
            }}
          />
        </div>

        <div className="form-group" style={{ marginBottom: '25px' }}>
          <label style={{ 
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#e0e0e0'
          }}>
            Custom Message:
            {invoice.default_email_message && emailForm.message && (
              <span style={{ 
                fontWeight: 'normal',
                fontSize: '14px',
                color: '#00897b',
                marginLeft: '10px'
              }}>
                (Pre-filled with default template)
              </span>
            )}
            {messageTemplate && (
              <button
                type="button"
                onClick={resetMessageToTemplate}
                style={{
                  float: 'right',
                  backgroundColor: '#1e88e5',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Reset to Template
              </button>
            )}
          </label>
          <div style={{ 
            fontSize: '12px', 
            color: '#888', 
            marginBottom: '8px',
            fontStyle: 'italic'
          }}>
            Tip: Use {'{INVOICE_NUMBER}'} in your message to automatically insert the invoice number ({getInvoiceNumber(invoice)})
          </div>
          <textarea
            value={emailForm.message}
            onChange={(e) => handleMessageChange(e.target.value)}
            placeholder="Add a custom message to the email..."
            rows="8"
            style={{
              width: '100%',
              padding: '10px 15px',
              border: '1px solid #333',
              borderRadius: '6px',
              fontSize: '16px',
              transition: 'border-color 0.3s ease',
              resize: 'vertical',
              backgroundColor: '#0f0f0f',
              color: '#e0e0e0'
            }}
          />
        </div>

        <button 
          onClick={sendEmail} 
          disabled={sendingEmail || !emailForm.recipientEmail}
          className="button primary"
          style={{
            backgroundColor: sendingEmail || !emailForm.recipientEmail ? '#424242' : '#00897b',
            color: 'white',
            padding: '12px 30px',
            borderRadius: '6px',
            border: 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: sendingEmail || !emailForm.recipientEmail ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {sendingEmail ? (
            <>
              <span style={{
                display: 'inline-block',
                width: '16px',
                height: '16px',
                border: '2px solid transparent',
                borderTopColor: 'white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></span>
              Sending...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
              Send Invoice Email
            </>
          )}
        </button>
      </div>

      <div style={{ marginTop: '40px', textAlign: 'center' }}>
        <a 
          href="/invoice" 
          className="button secondary"
          style={{
            backgroundColor: '#2a2a2a',
            color: '#e0e0e0',
            padding: '12px 30px',
            borderRadius: '6px',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            border: '1px solid #444'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Invoice Generator
        </a>
      </div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        html, body {
          background-color: #0a0a0a !important;
          margin: 0;
          padding: 0;
        }
        
        #root {
          background-color: #0a0a0a !important;
          min-height: 100vh;
        }
        
        input:focus, textarea:focus {
          outline: none;
          border-color: #1e88e5 !important;
          box-shadow: 0 0 0 2px rgba(30, 136, 229, 0.2);
        }
        
        input::placeholder, textarea::placeholder {
          color: #666;
          opacity: 1;
        }
        
        .button:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.6) !important;
          filter: brightness(1.2);
        }
        
        .button:active {
          transform: translateY(0);
        }
        
        /* Scrollbar styling for dark theme */
        ::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        
        ::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #444;
          border-radius: 5px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </div>
  );
};

export default InvoicePreviewPage;